import { Location } from '@cobliteam/common-types';
import type { RouteDef, RuntimeFlags } from '@cobliteam/dashboard/src/dashboardDef.type';

// hasCameraFlag and hasRedeCan are flags that are used to determine if a route is visible or not
const RUNTIME_FLAG_KEYS: (keyof RuntimeFlags)[] = [
  'hasCameraFlag',
  'hasRedeCan',
  'hasNoValidGroups',
  'hasGroupsRestriction',
  'isSafetyCoacher',
  'hasIdentifiers',
  'isOnboardingFleet',
];
const RUNTIME_FLAG_KEYS_WITH_INVERSE_LOGIC: (keyof RuntimeFlags)[] = ['hasGroupsRestriction']; // These flags show routes when runtime flag is false

const isVisible = (routeOrGroup: any, runtimeFlags: RuntimeFlags) => {
  return RUNTIME_FLAG_KEYS.every((flagKey) => {
    if (routeOrGroup[flagKey] !== undefined) {
      // For flags with inverse logic: route shows when runtime flag is false
      if (RUNTIME_FLAG_KEYS_WITH_INVERSE_LOGIC.includes(flagKey)) {
        return runtimeFlags[flagKey] === false;
      }

      // For all other flags: exact match between route flag and runtime flag
      return runtimeFlags[flagKey] === routeOrGroup[flagKey];
    }
    return true;
  });
};

const pruneToBoletos = (routesDef: RouteDef[]): RouteDef[] =>
  routesDef.reduce<RouteDef[]>((acc, route) => {
    if (route.url === Location.BUSINESS_MANAGER_BOLETOS) {
      acc.push(route);
      return acc;
    }

    const prunedChildren = route.routes ? pruneToBoletos(route.routes) : [];
    if (prunedChildren.length) acc.push({ ...route, routes: prunedChildren });

    return acc;
  }, []);

const getInactiveFleetRoutes = (routesDef: RouteDef[]): RouteDef[] => {
  const prunedSections = pruneToBoletos(routesDef);
  // Preferência pela seção customer-area: o grupo financeiro dela (ao contrário do de config)
  // não carrega feature flag, então a entrada de boletos sobrevive ao getRoutesWithFeatureFlag.
  const preferredSection = prunedSections.find((section) => section.id === 'customer-area') ?? prunedSections[0];
  if (!preferredSection) return [];

  // A seção customer-area vem com sidebar.hide e featureFlag businessManager; no modo
  // frota inativa a entrada de boletos precisa aparecer no menu incondicionalmente.
  return [
    {
      ...preferredSection,
      featureFlag: undefined,
      sidebar: { ...preferredSection.sidebar, hide: false },
    },
  ];
};

const getRoutesWithPermissionAndFeatureFlagAndRuntimeFlag = (routesDef: RouteDef[], runtimeFlags: RuntimeFlags) => {
  // Frota inativa: o menu se resume à página de boletos (antes do gate de grupos —
  // mesmo sem grupos válidos, a frota inativa precisa navegar até os boletos)
  if (runtimeFlags.isInactiveFleet) return getInactiveFleetRoutes(routesDef);
  if (runtimeFlags.hasNoValidGroups) return [];

  const routesDefWithRuntimeFlag = routesDef.reduce<RouteDef[]>((acc, route) => {
    const routesWithRuntimeFlag = route.routes?.filter((subRoute) => {
      subRoute.routes = subRoute.routes?.filter((group) => isVisible(group, runtimeFlags));
      return isVisible(subRoute, runtimeFlags);
    });

    if (routesWithRuntimeFlag?.length) {
      acc.push({ ...route, routes: routesWithRuntimeFlag });
      return acc;
    }

    if (isVisible(route, runtimeFlags)) acc.push(route);

    return acc;
  }, []);

  return routesDefWithRuntimeFlag;
};

export default getRoutesWithPermissionAndFeatureFlagAndRuntimeFlag;
